/**
 * In-memory workspace tags for agent_chat threads.
 * Used so last-thread restore and composer chips can honor tags without
 * waiting on the Chats sidebar filter.
 */

import { syncAgentPanelLastThreadMeta } from '@/lib/agent-panel-last-thread'

export type ThreadWorkspaceMeta = {
  threadId: string
  linkedConversationId?: string
  workspaceId: string | null
  taggedOrganizationIds: string[]
}

const byThreadId = new Map<string, ThreadWorkspaceMeta>()
const byConversationId = new Map<string, ThreadWorkspaceMeta>()

function linkedConversationIdFromThread(thread: {
  linkedConversationId?: string
  metadata?: Record<string, unknown>
}): string | undefined {
  if (typeof thread.linkedConversationId === 'string' && thread.linkedConversationId.trim()) {
    return thread.linkedConversationId
  }
  const fromMeta = thread.metadata?.linked_conversation_id
  return typeof fromMeta === 'string' && fromMeta.trim() ? fromMeta : undefined
}

export function rememberThreadWorkspaceMeta(thread: {
  id: string
  workspaceId?: string | null
  organizationId?: string | null
  taggedOrganizationIds?: string[] | null
  linkedConversationId?: string
  metadata?: Record<string, unknown>
}): ThreadWorkspaceMeta {
  const linkedConversationId = linkedConversationIdFromThread(thread)
  const incomingTags = Array.isArray(thread.taggedOrganizationIds)
    ? thread.taggedOrganizationIds.filter(Boolean).map(String)
    : []
  const existing = byThreadId.get(thread.id) ?? (linkedConversationId
    ? byConversationId.get(linkedConversationId)
    : undefined)
  const orgFallback = thread.organizationId ? [String(thread.organizationId)] : []
  const taggedOrganizationIds =
    incomingTags.length > 0
      ? incomingTags
      : orgFallback.length > 0
        ? orgFallback
        : (existing?.taggedOrganizationIds ?? [])
  const meta: ThreadWorkspaceMeta = {
    threadId: thread.id,
    linkedConversationId,
    workspaceId: thread.workspaceId ?? existing?.workspaceId ?? null,
    taggedOrganizationIds,
  }
  byThreadId.set(thread.id, meta)
  if (linkedConversationId) {
    byConversationId.set(linkedConversationId, meta)
  }
  syncAgentPanelLastThreadMeta({
    id: thread.id,
    taggedOrganizationIds: meta.taggedOrganizationIds,
    workspaceId: meta.workspaceId,
    organizationId: thread.organizationId ?? null,
  })
  return meta
}

export function peekThreadWorkspaceMeta(threadId: string | null | undefined): ThreadWorkspaceMeta | null {
  if (!threadId) return null
  return byThreadId.get(threadId) ?? byConversationId.get(threadId) ?? null
}

export function peekThreadWorkspaceMetaByConversationId(
  conversationId: string | null | undefined,
): ThreadWorkspaceMeta | null {
  if (!conversationId) return null
  return byConversationId.get(conversationId) ?? null
}

/** Test-only. */
export function resetThreadWorkspaceMetaCache(): void {
  byThreadId.clear()
  byConversationId.clear()
}
